/**
 *
 * Onboard Google Cloud
 *
 */

import React, {
  memo,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';

import { CloudProviders } from '@ariksa/cloud-account';
import { Screens } from '@ariksa/inventory-core';
import { Alert, AlertIcon, Box, HStack } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { PageHeaderWithIcon } from 'components/DataDisplay';
import { OutlinedIconButton } from 'components/DataEntry/Button/OutlinedIconButton';
import { Form } from 'components/DataEntry/Form';
import {
  CopyIcon,
  GoogleCloudIcon,
  PermissionsActionsIcon,
  QuestionCircleIcon,
  ThirdPartyIcon,
} from 'components/Icons';
import { errorToast } from 'components/Toast';
import { selectApp } from 'containers/App/selectors';
import { actions as appActions } from 'containers/App/slice';
import { OnboardingHelp } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/OnboardAws/types';
import { gcpHelpHeaderMapping } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/OnboardGcp/Help/utils';
import { GCPOnboardingHelp } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/OnboardGcp/types';
import { renderRequiredSteps } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/OnboardGcp/utils';
import { ReadOnlyTag } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/ReadOnlyTag';
import { stopSignWithMessage } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/utils';
import { selectCloudAccountWizard } from 'containers/Setup/CloudAccounts/CloudAccountWizard/selectors';
import { actions } from 'containers/Setup/CloudAccounts/CloudAccountWizard/slice';
import { selectSetup } from 'containers/Setup/selectors';
import { actions as setupActions } from 'containers/Setup/slice';

export const OnboardGCPForm = memo(() => {
  const navigate = useNavigate();
  const { onboardingAccount } = useSelector(selectCloudAccountWizard);
  const { configCommands } = useSelector(selectSetup);
  const { cloudAccounts } = useSelector(selectApp);
  const dispatch = useDispatch();
  const [accountNameUsedError, setAccountNameUsedError] = useState('');
  const [name, setName] = useState('');
  const [serviceAccountEmailID, setServiceAccountEmailID] = useState('');
  const [copied, setCopied] = useState('');

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      name: '',
      accountNameUsedError: '',
      isConfirm: false,
      enabledGoogleApis: false,
      isServiceAccountCreated: false,
      showHelp: GCPOnboardingHelp.None,
      serviceAccountCommand: '',
      enableGoogleApiCommand:
        'gcloud services enable iam.googleapis.com iamcredentials.googleapis.com ' +
        'compute.googleapis.com cloudasset.googleapis.com deploymentmanager.googleapis.com ' +
        'cloudresourcemanager.googleapis.com',
      serviceAccountEmailID: '',
      hasError: false,
    },
  );

  // get account config
  useEffect(() => {
    dispatch(
      setupActions.getConfigCommands({
        q: {
          screen: Screens.Onboarding,
        },
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    updateState({
      serviceAccountCommand:
        configCommands.data?.cloud_commands_map?.gcp?.onboarding
          ?.create_service_account,
      serviceAccountEmailID:
        configCommands.data?.cloud_commands_map?.gcp?.onboarding
          ?.fetch_service_account,
    });
  }, [configCommands.data]);

  const enableGoogleApiSteps = [
    {
      label: 'I have enabled Google APIs',
      helpHeader: 'Enable Google APIs',
      secondary: [
        {
          label: 'Copy command to enable Google APIs',
          icon: <CopyIcon p={0.5} />,
          tooltip: 'Copy command',
          copyText: state.enableGoogleApiCommand,
        },
        {
          label: 'Redirect to Google Cloud console',
          icon: <ThirdPartyIcon />,
          url: 'https://console.cloud.google.com',
          tooltip: 'Redirect to Google console. Manually execute command',
        },
      ],
    },
  ];

  const createServiceAccountSteps = [
    {
      label: 'I have created a service account for Ariksa',
      secondary: [
        {
          label: 'Copy command to create service account',
          icon: <CopyIcon p={0.5} />,
          tooltip: 'Copy command',
          copyText: state.serviceAccountCommand,
        },
        {
          label: 'Redirect to Google Cloud console',
          icon: <ThirdPartyIcon />,
          url: 'https://console.cloud.google.com',
          tooltip: 'Redirect to Google console. Manually execute command',
        },
      ],
    },
  ];

  const handleSubmit = data => {
    const { accountName, serviceAccountEmailID } = data;

    const onError = err => {
      updateState({ hasError: true });
      if (err?.code === 409) {
        setAccountNameUsedError(err.description ?? '-');
      } else {
        errorToast({ title: err.title, description: err.description });
      }
    };

    dispatch(
      actions.onboardCloudAccount({
        q: {
          cloudAccountCreateRequest: {
            name: accountName,
            cloud_type: CloudProviders.Gcp,
            gcp: {
              service_account_email: serviceAccountEmailID,
            },
          },
        },
        onSuccess: () => navigate('/setup/accounts'),
        onError,
      }),
    );
  };
  const handleReset = () => {
    navigate('/setup/accounts');
    if (isEmpty(cloudAccounts.data))
      dispatch(appActions.updateShowGettingStarted(true));
  };

  const toggleHelp = useCallback(
    (helpType: GCPOnboardingHelp) => {
      updateState({
        showHelp:
          state.showHelp === helpType ? GCPOnboardingHelp.None : helpType,
      });
    },
    [state.showHelp],
  );

  return (
    <>
      <Form
        title={
          <HStack w="full" justify="space-between">
            <Box>Add Google Cloud Project</Box>
            <OutlinedIconButton
              leftIcon={<PermissionsActionsIcon p={1} />}
              onClick={() =>
                updateState({ showHelp: OnboardingHelp.Permission })
              }
            >
              Show Permissions
            </OutlinedIconButton>
          </HStack>
        }
        titleIcon={<GoogleCloudIcon />}
        isTitleIconFilled={false}
        schema={{
          accountName: {
            type: 'text',
            label: 'Connector name',
            //tooltip: 'Only Alphanumeric, hyphen is allowed.',
            isRequired: true,
            error: accountNameUsedError,
            onChange: value => {
              updateState({ hasError: false });
              setName(value);
              if (value.match(/^[a-zA-Z0-9-]*$/i)) setAccountNameUsedError('');
              else
                setAccountNameUsedError(
                  'Only Alphanumeric, hyphen is allowed!',
                );
            },
            autoFocus: true,
          },
          onboardingMode: {
            type: 'custom-with-form-control',
            label: 'Onboarding mode',
            component: <ReadOnlyTag />,
          },
          confirmMessage: {
            type: 'custom',
            component: () =>
              stopSignWithMessage(
                'Review, execute and checkbox each step to proceed to the next step',
                'Confirm that you have permissions to onboard a project',
                state.isConfirm,
                val => {
                  updateState({ isConfirm: val, hasError: false });
                  if (!val)
                    updateState({
                      isServiceAccountCreated: false,
                      enabledGoogleApis: false,
                    });
                },
                () => toggleHelp(GCPOnboardingHelp.CheckPermissions),
              ),
          },
          enableGoogleApiSteps: {
            type: 'custom',
            component: () =>
              renderRequiredSteps({
                steps: enableGoogleApiSteps,
                onClickButton: () =>
                  toggleHelp(GCPOnboardingHelp.EnableGoogleAPIs),
                copied,
                setCopied,
                isDisabled: !state.isConfirm,
                isChecked: state.enabledGoogleApis,
                setIsChecked: val => {
                  updateState({ enabledGoogleApis: val, hasError: false });
                  if (!val) updateState({ isServiceAccountCreated: false });
                },
              }),
          },
          createServiceAccountSteps: {
            type: 'custom',
            component: () =>
              renderRequiredSteps({
                steps: createServiceAccountSteps,
                onClickButton: () =>
                  toggleHelp(GCPOnboardingHelp.CreateServiceAccount),
                copied,
                setCopied,
                isDisabled: !state.isConfirm || !state.enabledGoogleApis,
                isChecked: state.isServiceAccountCreated,
                setIsChecked: val =>
                  updateState({
                    isServiceAccountCreated: val,
                    hasError: false,
                  }),
              }),
          },
          ...(!!state.isServiceAccountCreated
            ? {
                serviceAccountEmailID: {
                  type: 'text',
                  label: 'Service Account - Email ID',
                  onChange: value => {
                    setServiceAccountEmailID(value);
                    updateState({ hasError: false });
                  },
                  helpIcon: <QuestionCircleIcon />,
                  helpTooltip: 'Get Service Account Email ID',
                  onClickHelpIcon: () => {
                    toggleHelp(GCPOnboardingHelp.ServiceAccountEmailID);
                  },
                },
              }
            : {}),
          ...(state.hasError
            ? {
                validatedMessage: {
                  type: 'custom',
                  component: () => {
                    return (
                      <Box w="full">
                        <Alert
                          status={'error'}
                          borderRadius={6}
                          title={onboardingAccount.error?.title}
                        >
                          <AlertIcon />
                          {onboardingAccount.error?.description}
                        </Alert>
                      </Box>
                    );
                  },
                },
              }
            : {}),
        }}
        isLoading={configCommands.isLoading}
        buttonOptions={{
          submit: {
            name: 'CONNECT',
            isLoading: configCommands.isLoading || onboardingAccount.isLoading,
            isDisabled:
              configCommands.isLoading ||
              onboardingAccount.isLoading ||
              !!accountNameUsedError ||
              !name ||
              !state.isConfirm ||
              !state.enabledGoogleApis ||
              !state.isServiceAccountCreated ||
              !serviceAccountEmailID,
          },
          reset: {
            isVisible: true,
            onClick: handleReset,
          },
        }}
        helpDrawer={{
          isOpen: state.showHelp !== GCPOnboardingHelp.None,
          onClose: () => updateState({ showHelp: GCPOnboardingHelp.None }),
          content: gcpHelpHeaderMapping(
            state.showHelp,
            state.serviceAccountCommand,
            state.serviceAccountEmailID,
          )?.content,
          header: (
            <PageHeaderWithIcon
              label={
                gcpHelpHeaderMapping(
                  state.showHelp,
                  state.serviceAccountCommand,
                  state.serviceAccountEmailID,
                )?.header
              }
              icon={
                gcpHelpHeaderMapping(
                  state.showHelp,
                  state.serviceAccountCommand,
                  state.serviceAccountEmailID,
                )?.icon
              }
              reversed
              fontWeight={600}
              fontSize="lg"
            />
          ),
          styles: { body: { pt: 6 } },
        }}
        styles={{
          form: { formWidth: '600px', title: { labelStyles: { w: 'full' } } },
        }}
        handleSubmit={handleSubmit}
      />
    </>
  );
});
